import React, { useEffect, useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { AddTariffForm, createAddTariffSchema, AddTariffFormData } from './AddTariffForm'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, FormProvider } from 'react-hook-form'
import { useAddTariff } from '../../api/use-add-tariff'
import { tariffsFormToDto } from '../../transformers/tariffs-form-to-dto-model'

export interface AddTariffDialogProps {
  open: boolean
  onClose: () => void
  place: number
  installationId: number
  currencySymbol: string | undefined
}

/**
 * Dialog component for add new tariff which is open on click add tariff button.
 * It holds add tariff form with Formik wrapper, initial values, validation schema and submit handler
 */
export const AddTariffDialog: React.FC<AddTariffDialogProps> = ({ open, onClose, place, installationId, currencySymbol }) => {
  const { t } = useCustomTranslation()
  const addTariffSchema = useMemo(() => createAddTariffSchema(t), [t])

  const form = useForm<AddTariffFormData>({
    resolver: zodResolver(addTariffSchema),
    defaultValues: {
      tariffName: '',
      tariffGridPrice: '',
      tariffSolarPrice: '',
      tariffDischargePrice: '',
      startDate: null,
      endDate: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const {
    trigger,
    formState: { isDirty, isValid },
    getValues,
  } = form

  const { isLoading, addTariff } = useAddTariff(onClose, installationId)

  const handleSubmit = async (): Promise<void> => {
    const formData = getValues()
    const payload = tariffsFormToDto(formData, installationId)
    addTariff(payload)
  }

  /**
   * Make sure that error messages for required fields are triggered even when required
   * fields are not touched, e.g. user only set the discharge price and expects that the
   * form is valid because there are no error messages shown.
   */
  useEffect(() => {
    if (isDirty) {
      trigger()
    }
  }, [isDirty, trigger])

  return (
    <FormProvider {...form}>
      <Dialog
        id="e-mobility-add-tariff-dialog"
        maxWidth="sm"
        title={t('emobility.tariffs.addTariffTitle')}
        onClose={onClose}
        place={place}
        actions={{
          primary: {
            name: t('emobility.tariffs.add'),
            onClick: handleSubmit,
            disabled: !isDirty || !isValid || isLoading,
            isLoading,
          },
          secondary: {
            name: t('emobility.tariffs.cancel'),
          },
        }}
      >
        <AddTariffForm currencySymbol={currencySymbol} />
      </Dialog>
    </FormProvider>
  )
}
